import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/functions/supabase/supabase';
import type { Database } from '@/types/supabase';
import type { EmailTemplateRecord, Template } from '@/types/email';

type EmailTemplateRow =
  Database['public']['Tables']['email_templates']['Row'];

export const useEmailTemplates = () => {
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('email_templates')
        .select('id, name, subject, body, type, is_active')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (queryError) {
        throw queryError;
      }

      const rows = (data ?? []) as EmailTemplateRow[];
      const nextTemplates = rows.map(
        (row): EmailTemplateRecord => ({
          id: row.id,
          template: row.name as Template,
          subject: row.subject,
          body: row.body,
          type: row.type ?? null,
        })
      );

      setTemplates(nextTemplates);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught
          : new Error('Unknown error while fetching email templates')
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
  };
};

export type UseEmailTemplatesResult = ReturnType<typeof useEmailTemplates>;
